import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { getUserPermissions, hasPermission, hasAnyPermission } from "./rbac";
import { MODULE_STATE, getModuleState } from "./modules";
import Unauthorized from "@/layout/error/component/Unauthorized";


/**
 * PermissionGuard
 *
 * Centralized route protection and authorization checks.
 *
 * Props:
 * - permission: string (resource.action)
 * - permissions: string[] (any-of)
 * - module: string (a MODULES code) — not entitled renders Unauthorized
 * - redirectTo: where to send a caller who is not signed in at all
 *   (default: /login). Nothing to do with the unauthorized case — that one
 *   renders `Unauthorized`, which resolves its own way back.
 *
 * The module test is entitlement only. A merely unavailable module still
 * loads its page: a kill switch stops new work, never a read, and the tenant
 * keeps everything they generated before it was flipped.
 */
const PermissionGuard = ({
    permission = '',
    permissions = [],
    module = '',
    redirectTo = "/login",
    isComponent = false,
    children,
}) => {
    const { user, isAuthenticated, isLoading } = useAuth();

    // While auth is bootstrapping, keep UI stable.
    if (isLoading) return null;

    // If not logged in, allow AuthGuard to handle it.
    if (!isAuthenticated) return <Navigate to={redirectTo} replace />;

    // `Unauthorized` resolves its own way back, through `resolveLandingPath`,
    // so it lands the user somewhere their account can actually open. It used
    // to be handed the bare prop name here, which JSX reads as `true`.
    if (module && getModuleState(module, user) === MODULE_STATE.NOT_ENTITLED) {
        return isComponent ? null : <Unauthorized />;
    }

    // A route may carry a module and no permission — the module is then the
    // whole gate, and an empty permission list must not read as "denied".
    if (!permission && !permissions?.length) return children ?? null;

    const userPermissions = getUserPermissions(user);

    const allowed = permission
        ? hasPermission(permission, userPermissions)
        : permissions
            ? hasAnyPermission(permissions, userPermissions)
            : false;

    if (allowed) return children ?? null;

    return isComponent ? null : <Unauthorized />;
};

export default PermissionGuard;